import { Guide, GuideCategory } from '../HelpCenterTypes';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/store/hooks';
import { selectUser } from '../../../auth/user/store/userSlice'; 
import { useMemo } from 'react';

// Hook to get internationalized articles and categories with role-based filtering
export const useInternationalizedArticles = () => {
  const { t, i18n } = useTranslation('helpCenter');
  const currentLanguage = i18n.language;
  const user = useAppSelector(selectUser);

  // Get user's current role from current organization (exact match only, no inheritance)
  const userRoles = useMemo(() => {
    if (!user?.org_id_to_org_member_info) return [];
    
    const orgIds = Object.keys(user.org_id_to_org_member_info);
    const currentOrgId = orgIds[0]; // Assuming first org is current
    const currentOrg = user.org_id_to_org_member_info[currentOrgId];
    
    // Use only the current role, not inherited roles to ensure exact role-based filtering
    const currentRole = currentOrg?.user_role;
    return currentRole ? [currentRole] : [];
  }, [user]);

  const categories: GuideCategory[] = [
    {
      id: '1',
      slug: 'getting-started',
      title: t('GETTING_STARTED'),
      description: t('GETTING_STARTED_DESCRIPTION'),
      icon: 'heroicons-outline:rocket-launch'
    },
    {
      id: '2',
      slug: 'cleaning-management',
      title: t('CLEANING_MANAGEMENT'),
      description: t('CLEANING_MANAGEMENT_DESCRIPTION'),
      icon: 'heroicons-outline:user-circle'
    },
    {
      id: '3',
      slug: 'account-management',
      title: t('ACCOUNT_MANAGEMENT'),
      description: t('ACCOUNT_MANAGEMENT_DESCRIPTION'),
      icon: 'heroicons-outline:user-circle'
    },
    {
      id: '4',
      slug: 'technical-support',
      title: t('TECHNICAL_SUPPORT'),
      description: t('TECHNICAL_SUPPORT_DESCRIPTION'),
      icon: 'heroicons-outline:user-circle'
    },
    // {
    //   id: '3',
    //   slug: 'features',
    //   title: t('FEATURES_AND_TOOLS'),
    //   description: t('FEATURES_AND_TOOLS_DESCRIPTION'),
    //   icon: 'heroicons-outline:puzzle-piece'
    // },
    // {
    //   id: '4',
    //   slug: 'troubleshooting',
    //   title: t('TROUBLESHOOTING'),
    //   description: t('TROUBLESHOOTING_DESCRIPTION'),
    //   icon: 'heroicons-outline:wrench-screwdriver'
    // }
  ];

  // Helper function to get language-specific images
  const getLanguageSpecificImages = (articleSlug: string) => {
    const imageConfigs = {
      'hotel-settings': {
        en: [
          {
            id: '1',
            src: '/assets/images/help-center/hotel-settings-en.png',
            alt: 'Welcome dashboard overview in English',
            caption: 'Your dashboard will look like this after setup'
          },
        ],
        bg: [
          {
            id: '1',
            src: '/assets/images/help-center/hotel-settings-bg.png',
            alt: 'Преглед на таблото за добре дошли на български',
            caption: 'Вашето табло ще изглежда така след настройка'
          },
        ]
      },
      'password-security-best-practices': {
        en: [
          {
            id: '1',
            src: 'https://via.placeholder.com/800x400/DC2626/FFFFFF?text=Password+Strength+EN',
            alt: 'Password strength indicator in English',
            caption: 'Our password strength indicator helps you create secure passwords'
          }
        ],
        bg: [
          {
            id: '1',
            src: 'https://via.placeholder.com/800x400/DC2626/FFFFFF?text=Сила+на+Парола+BG',
            alt: 'Индикатор за сила на парола на български',
            caption: 'Нашият индикатор за сила на парола ви помага да създавате сигурни пароли'
          }
        ]
      },
      'advanced-search-techniques': {
        en: [
          {
            id: '1',
            src: 'https://via.placeholder.com/800x400/7C3AED/FFFFFF?text=Search+Interface+EN',
            alt: 'Advanced search interface in English',
            caption: 'The advanced search interface with filters and operators'
          },
          {
            id: '2',
            src: 'https://via.placeholder.com/800x400/059669/FFFFFF?text=Search+Results+EN',
            alt: 'Search results with filters in English',
            caption: 'Filtered search results showing relevant content'
          }
        ],
        bg: [
          {
            id: '1',
            src: 'https://via.placeholder.com/800x400/7C3AED/FFFFFF?text=Интерфейс+за+Търсене+BG',
            alt: 'Разширен интерфейс за търсене на български',
            caption: 'Разширеният интерфейс за търсене с филтри и оператори'
          },
          {
            id: '2',
            src: 'https://via.placeholder.com/800x400/059669/FFFFFF?text=Резултати+от+Търсене+BG',
            alt: 'Резултати от търсене с филтри на български',
            caption: 'Филтрирани резултати от търсене, показващи релевантно съдържание'
          }
        ]
      },
      'common-login-issues': {
        en: [
          {
            id: '1',
            src: 'https://via.placeholder.com/800x400/EA580C/FFFFFF?text=Login+Error+EN',
            alt: 'Login error message in English',
            caption: 'Common login error messages and what they mean'
          }
        ],
        bg: [
          {
            id: '1',
            src: 'https://via.placeholder.com/800x400/EA580C/FFFFFF?text=Грешка+при+Влизане+BG',
            alt: 'Съобщение за грешка при влизане на български',
            caption: 'Често срещани съобщения за грешки при влизане и какво означават'
          }
        ]
      }
    };

    return imageConfigs[articleSlug]?.[currentLanguage] || imageConfigs[articleSlug]?.en || [];
  };

  const articles: Guide[] = [
    {
      id: '1',
      categoryId: '1',
      categorySlug: 'getting-started',
      slug: 'hotel-settings',
      title: t('WELCOME_TO_OUR_PLATFORM'),
      subtitle: t('WELCOME_TO_OUR_PLATFORM_SUBTITLE'),
      content: t('WELCOME_CONTENT'),
      images: getLanguageSpecificImages('hotel-settings'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin']
    },
    {
      id: '2',
      categoryId: '1',
      categorySlug: 'getting-started',
      slug: 'team-management',
      title: t('TEAM_MANAGEMENT'),
      subtitle: t('TEAM_MANAGEMENT_SUBTITLE'),
      content: t('TEAM_MANAGEMENT_CONTENT'),
      images: getLanguageSpecificImages('team-management'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin'] 
    },
    {
      id: '3',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'assignments-management',
      title: t('ASSIGNMENTS_MANAGEMENT'),
      subtitle: t('ASSIGNMENTS_MANAGEMENT_SUBTITLE'),
      content: t('ASSIGNMENTS_MANAGEMENT_CONTENT'),
      images: getLanguageSpecificImages('assignments-management'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Main Cleaner', 'Reception']
    },
    {
      id: '4',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'cleaning-sessions',
      title: t('CLEANING_SESSIONS'),
      subtitle: t('CLEANING_SESSIONS_SUBTITLE'),
      content: t('CLEANING_SESSIONS_CONTENT'),
      images: getLanguageSpecificImages('cleaning-sessions'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Main Cleaner', 'Reception']
    },
    {
      id: '5',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'daily-assignments-list',
      title: t('DAILY_ASSIGNMENTS_LIST'),
      subtitle: t('DAILY_ASSIGNMENTS_LIST_SUBTITLE'),
      content: t('DAILY_ASSIGNMENTS_LIST_CONTENT'),
      images: getLanguageSpecificImages('overview'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Main Cleaner', 'Reception']
    },
    {
      id: '6',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'daily-overview',
      title: t('OVERVIEW'),
      subtitle: t('OVERVIEW_SUBTITLE'),
      content: t('OVERVIEW_CONTENT'),
      images: getLanguageSpecificImages('overview'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Reception', 'Main Cleaner'] 
    },
    {
      id: '7',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'roomie-ai-intelligent-assistant',
      title: t('ROOMIE_AI_INTELLIGENT_ASSISTANT'),
      subtitle: t('ROOMIE_AI_INTELLIGENT_ASSISTANT_SUBTITLE'),
      content: t('ROOMIE_AI_INTELLIGENT_ASSISTANT_CONTENT'),
      images: getLanguageSpecificImages('overview'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Reception', 'Main Cleaner'] 
    },
    {
      id: '8',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'cleaning-operations',
      title: t('CLEANING_OPERATIONS'),
      subtitle: t('CLEANING_OPERATIONS_SUBTITLE'),
      content: t('CLEANING_OPERATIONS_CONTENT'),
      images: getLanguageSpecificImages('overview'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Main Cleaner']
    },
    {
      id: '9',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'cleaning-page-cleaner',
      title: t('CLEANING_PAGE_CLEANER'),
      subtitle: t('CLEANING_PAGE_CLEANER_SUBTITLE'),
      content: t('CLEANING_PAGE_CLEANER_CONTENT'),
      images: getLanguageSpecificImages('overview'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Cleaner', 'Main Cleaner'] // Cleaner specific page
    },
    {
      id: '10',
      categoryId: '2',
      categorySlug: 'cleaning-management',
      slug: 'cleaner-submit-problem',
      title: t('CLEANER_SUBMIT_PROBLEM'),
      subtitle: t('CLEANER_SUBMIT_PROBLEM_SUBTITLE'),
      content: t('CLEANER_SUBMIT_PROBLEM_CONTENT'),
      images: getLanguageSpecificImages('overview'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Cleaner', 'Main Cleaner'] // Cleaner specific page
    },
    {
      id: '11',
      categoryId: '3',
      categorySlug: 'account-management',
      slug: 'profile-setup',
      title: t('PROFILE_SETUP_GUIDE'),
      subtitle: t('PROFILE_SETUP_GUIDE_DESC'),
      content: t('PROFILE_SETUP_GUIDE_CONTENT'),
      images: getLanguageSpecificImages('account-management'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Reception', 'Cleaner', 'Main Cleaner', 'Tech Support']
    },
    {
      id: '12',
      categoryId: '4',
      categorySlug: 'technical-support',
      slug: 'technical-support',
      title: t('TECHNICAL_SUPPORT_DASHBOARD'),
      subtitle: t('TECHNICAL_SUPPORT_SUBTITLE'),
      content: t('TECHNICAL_SUPPORT_CONTENT'),
      images: getLanguageSpecificImages('technical-support'),
      author: 'BIODIT',
      publishedAt: '2025-08-07',
      updatedAt: '2025-08-07',
      tags: ['welcome', 'getting-started', 'beginner', 'onboarding'],
      readingTime: 8,
      featured: true,
      roles: ['Godfather', 'Admin', 'Tech Support']
    }
    // {
    //   id: '2',
    //   categoryId: '2',
    //   categorySlug: 'account-management',
    //   slug: 'password-security-best-practices',
    //   title: t('PASSWORD_SECURITY_BEST_PRACTICES'),
    //   subtitle: t('PASSWORD_SECURITY_BEST_PRACTICES_SUBTITLE'),
    //   content: t('PASSWORD_CONTENT'),
    //   images: getLanguageSpecificImages('password-security-best-practices'),
    //   links: [
    //     {
    //       id: '1',
    //       title: t('SETTING_UP_TWO_FACTOR'),
    //       url: '/help-center/guides/account-management/two-factor-auth',
    //       description: t('SETTING_UP_TWO_FACTOR_DESC')
    //     },
    //     {
    //       id: '2',
    //       title: t('PASSWORD_RESET_GUIDE'),
    //       url: '/help-center/guides/account-management/reset-password',
    //       description: t('PASSWORD_RESET_GUIDE_DESC')
    //     }
    //   ],
    //   author: 'BIODIT',
    //   publishedAt: '2024-01-20',
    //   updatedAt: '2024-02-15',
    //   tags: ['security', 'password', 'authentication', 'safety'],
    //   readingTime: 5,
    //   featured: true
    // },
    // {
    //   id: '3',
    //   categoryId: '3',
    //   categorySlug: 'features',
    //   slug: 'advanced-search-techniques',
    //   title: t('ADVANCED_SEARCH_TECHNIQUES'),
    //   subtitle: t('ADVANCED_SEARCH_TECHNIQUES_SUBTITLE'),
    //   content: t('SEARCH_CONTENT'),
    //   images: getLanguageSpecificImages('advanced-search-techniques'),
    //   links: [
    //     {
    //       id: '1',
    //       title: t('SEARCH_FILTERS_GUIDE'),
    //       url: '/help-center/guides/features/search-filters',
    //       description: t('SEARCH_FILTERS_GUIDE_DESC')
    //     },
    //     {
    //       id: '2',
    //       title: t('SEARCH_TIPS_AND_TRICKS'),
    //       url: '/help-center/guides/features/search-tips',
    //       description: t('SEARCH_TIPS_AND_TRICKS_DESC')
    //     }
    //   ],
    //   author: 'BIODIT',
    //   publishedAt: '2024-02-10',
    //   updatedAt: '2024-03-05',
    //   tags: ['search', 'features', 'advanced', 'tips'],
    //   readingTime: 6
    // },
    // {
    //   id: '4',
    //   categoryId: '4',
    //   categorySlug: 'troubleshooting',
    //   slug: 'common-login-issues',
    //   title: t('COMMON_LOGIN_ISSUES'),
    //   subtitle: t('COMMON_LOGIN_ISSUES_SUBTITLE'),
    //   content: t('LOGIN_CONTENT'),
    //   images: getLanguageSpecificImages('common-login-issues'),
    //   links: [
    //     {
    //       id: '1',
    //       title: t('PASSWORD_RESET_GUIDE'),
    //       url: '/help-center/guides/account-management/reset-password',
    //       description: t('PASSWORD_RESET_GUIDE_DESC')
    //     },
    //     {
    //       id: '2',
    //       title: t('EMAIL_VERIFICATION_GUIDE'),
    //       url: '/help-center/guides/account-management/email-verification',
    //       description: t('EMAIL_VERIFICATION_GUIDE_DESC')
    //     },
    //     {
    //       id: '3',
    //       title: t('CONTACT_SUPPORT'),
    //       url: '/help-center/support',
    //       description: t('CONTACT_SUPPORT_DESC')
    //     }
    //   ],
    //   author: 'BIODIT',
    //   publishedAt: '2024-01-25',
    //   updatedAt: '2024-02-28',
    //   tags: ['login', 'troubleshooting', 'support', 'issues'],
    //   readingTime: 4
    // }
  ];

  // Filter articles based on user roles
  const filteredArticles = useMemo(() => {
    if (!userRoles.length) {
      // If no roles, show only articles with no role restrictions or show nothing
      return articles.filter(article => !article.roles || article.roles.length === 0);
    }

    return articles.filter(article => {
      // If article has no role restrictions, show to everyone
      if (!article.roles || article.roles.length === 0) {
        return true;
      }
      
      // Check if user has any of the required roles (exact match only)
      return article.roles.some(role => userRoles.some(userRole => userRole === role));
    });
  }, [articles, userRoles]);

  // Filter categories to only show those that have visible articles
  const filteredCategories = useMemo(() => {
    return categories.filter(category => 
      filteredArticles.some(article => article.categoryId === category.id)
    );
  }, [categories, filteredArticles]);

  return { categories: filteredCategories, articles: filteredArticles };
};

// Helper function to get articles by category
export const getArticlesByCategory = (articles: Guide[], categorySlug: string): Guide[] => {
  return articles.filter(article => article.categorySlug === categorySlug);
};

// Helper function to get featured articles
export const getFeaturedArticles = (articles: Guide[]): Guide[] => {
  return articles.filter(article => article.featured);
};

// Helper function to get article by slug
export const getArticleBySlug = (articles: Guide[], categorySlug: string, articleSlug: string): Guide | undefined => {
  return articles.find(article => 
    article.categorySlug === categorySlug && article.slug === articleSlug
  );
}; 